import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema.gen';

/**
 * Typed client for the smoke-backend REST API (paths, parameters and request bodies are
 * generated from api/openapi.json). Auth is the httpOnly `smoke_session` cookie; mutations
 * also carry the `X-CSRF-Token` header, obtained from login or GET /auth/session.
 */

export const API_BASE = '/api/v1';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** One problem in an error: `issue` is a stable code, `params` its values, `message` English. */
export interface ErrorDetail {
  path?: string;
  issue: string;
  params?: Record<string, string | number>;
  message?: string;
}

/** Stable error from the API body `{ error: { code, message, details? } }`. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: ErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function toApiError(response: Response, body?: unknown): Promise<ApiError> {
  let parsed = body;
  if (parsed === undefined) {
    try {
      parsed = await response.clone().json();
    } catch {
      parsed = undefined;
    }
  }
  const err = (
    parsed as { error?: { code?: unknown; message?: unknown; details?: unknown } } | undefined
  )?.error;
  if (err && typeof err.code === 'string') {
    return new ApiError(
      response.status,
      err.code,
      typeof err.message === 'string' ? err.message : err.code,
      Array.isArray(err.details) ? (err.details as ErrorDetail[]) : [],
    );
  }
  return new ApiError(
    response.status,
    response.status >= 500 ? 'INTERNAL_ERROR' : 'UNKNOWN',
    response.statusText,
  );
}

const csrfMiddleware: Middleware = {
  onRequest({ request }) {
    if (!SAFE_METHODS.has(request.method) && csrfToken) {
      request.headers.set('X-CSRF-Token', csrfToken);
    }
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: '', credentials: 'same-origin' });
api.use(csrfMiddleware);

type FetchResult = { data?: unknown; error?: unknown; response: Response };

/** Unwraps an openapi-fetch call: returns the typed body, or throws an {@link ApiError}. */
export async function call<T>(request: Promise<FetchResult>): Promise<T> {
  const { data, error, response } = await request;
  if (!response.ok) throw await toApiError(response, error);
  return data as T;
}

/** Raw request for multipart uploads (openapi-fetch serializes JSON). */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  const response = await fetch(path, {
    method: 'POST',
    body: form,
    headers,
    credentials: 'same-origin',
  });
  if (!response.ok) throw await toApiError(response);
  return (await response.json()) as T;
}
